import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ResponsiveLine } from '@nivo/line';
import { useTheme } from '@mui/material/styles';
import nivoTheme from '../../theme/nivoTheme';
import { DeltaChip } from './StatTile';

/**
 * Trend-Kachel (Charts 2.0): Verlauf mit Aussage statt nackter Linie —
 * Perioden-Summe + Delta im Kopf, 2px-Linie mit 10%-Flächen-Wash,
 * betonter Endpunkt (Ring + direkter Wert), Haarlinien-Grid, Achse unten.
 *
 *   label   – Kachel-Beschriftung
 *   points  – [{ x: 'Apr', y: 3 }, …] chronologisch
 *   color   – Linienfarbe (default: Teal — Alexanders Wahl)
 *   delta / deltaLabel – Veränderung gegen die Vorperiode (DeltaChip)
 *   value   – Kopf-Kennzahl (z. B. Perioden-Summe); optional
 */
export default function TrendTile({ label, points, color, delta, deltaLabel, value }) {
  const theme = useTheme();
  const c = color || theme.palette.secondary.main;
  const last = points.length ? points[points.length - 1] : null;

  // Endpunkt-Betonung: Ring in Flächenfarbe + direkter Wert am letzten Punkt
  const endPointLayer = ({ points: nivoPoints }) => {
    if (!nivoPoints.length) return null;
    const p = nivoPoints[nivoPoints.length - 1];
    return (
      <g>
        <circle cx={p.x} cy={p.y} r={4.5} fill={c} stroke={theme.palette.background.paper} strokeWidth={2} />
        <text x={p.x} y={p.y - 9} textAnchor="middle"
          style={{ fontSize: 10, fontWeight: 650, fill: theme.palette.text.primary, fontVariantNumeric: 'tabular-nums' }}>
          {last?.y}
        </text>
      </g>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 550, fontSize: '0.82rem', lineHeight: 1.5 }} noWrap>
        {label}
      </Typography>
      {value !== undefined && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
            {value}
          </Typography>
          <DeltaChip delta={delta} deltaLabel={deltaLabel} />
        </Box>
      )}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveLine
          theme={nivoTheme(theme)}
          data={[{ id: label, data: points }]}
          margin={{ top: 16, right: 14, bottom: 22, left: 14 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 0, max: 'auto' }}
          areaBaselineValue={0}
          curve="monotoneX"
          enableArea
          areaOpacity={0.1}
          enablePoints={false}
          enableGridX={false}
          enableGridY
          gridYValues={3}
          axisTop={null} axisRight={null} axisLeft={null}
          axisBottom={{ tickSize: 0, tickPadding: 8, tickRotation: 0 }}
          colors={[c]}
          lineWidth={2}
          layers={['grid', 'axes', 'areas', 'lines', endPointLayer, 'crosshair', 'mesh']}
          useMesh
          tooltip={({ point }) => (
            <Box sx={{ bgcolor: 'background.paper', px: 1.25, py: 0.75, borderRadius: 1.5, border: 1, borderColor: 'divider', fontSize: '0.8rem', boxShadow: 2 }}>
              <strong>{String(point.data.x)}</strong>: {String(point.data.y)}
            </Box>
          )}
        />
      </Box>
    </Box>
  );
}
